import {
  type BorrowingPowerInput,
  BorrowingPowerInputSchema,
  type BorrowingPowerOutput,
  type BorrowingPowerParsed,
  type BorrowingPowerRuleSet,
  type DtiFraming,
  type HemApproximationRow,
  type RepaymentStressInput,
  RepaymentStressInputSchema,
  type RepaymentStressOutput,
  type RepaymentStressParsed,
  type StressScenario,
} from "./types.js";

// ---------------------------------------------------------------------------
// Pure borrowing-power + repayment-stress engine. Same inputs + ruleset always
// yield the same output; no I/O, no Date.now() (the settings key is a
// parameter). The two surfaces share the standard amortisation primitive.
// ---------------------------------------------------------------------------

/**
 * HARD RULE (panel lens 2/3, BINDING — NCCP credit-assistance kill-line): a
 * personalised borrowing-power result + a paid broker/lender/loan-product
 * handoff = "credit assistance / arranging" requiring an ACL, with NO s765A
 * carve-out (a home loan is NCCP credit, not an s764A financial product). This
 * is the single highest-payout, sharpest-unguarded door in the suite — the
 * danger is DRIFT ("just add a broker tile to the result"). This guard runs
 * over every personalised result's serialised form and THROWS if any
 * broker / lender / credit-product / paid-handoff identifier leaks in. Affiliate
 * is out of scope v1 and lives, if ever, only on a separate generic non-
 * personalised surface under the NCCP mere-referral exemption — never here.
 * Tested in calculate.test.ts.
 */
// These are handoff-/CTA-shaped phrases, NOT bare nouns. The educational copy
// legitimately uses "lender(s)" and "broker" as plain words ("lenders assess
// differently", "lenders ration"); what the kill-line forbids is a paid HANDOFF
// — "talk to a broker", "apply now", "get pre-approved", "compare home loans",
// a recommendation to borrow. The guard targets those phrases so it catches a
// drifted broker tile without false-positiving on neutral explanation.
const FORBIDDEN_CREDIT_HANDOFF_TERMS = [
  "talk to a broker",
  "speak to a broker",
  "find a broker",
  "talk to a lender",
  "speak to a lender",
  "our broker",
  "our lender",
  "our partner",
  "apply now",
  "apply with",
  "apply for this loan",
  "get pre-approved",
  "get pre approved",
  "compare loans",
  "compare home loans",
  "loan product",
  "home loan offer",
  "refinance with",
  "we recommend",
  "you should borrow",
  "best rate for you",
  "cashback offer",
];

export function assertNoCreditHandoff(output: unknown): void {
  const serialised = JSON.stringify(output).toLowerCase();
  for (const term of FORBIDDEN_CREDIT_HANDOFF_TERMS) {
    if (serialised.includes(term)) {
      throw new Error(
        `borrowing-power result leaked a forbidden credit-assistance/handoff term: "${term}" — NCCP credit-assistance kill-line (no broker/lender/loan-product handoff may reach a personalised surface)`,
      );
    }
  }
}

function round(n: number): number {
  return Math.round(n);
}

/**
 * Standard P&I monthly repayment for a principal `p` at annual rate `r`
 * (fraction) over `years`. Closed-form amortisation:
 *   M = p · i(1+i)^n / ((1+i)^n − 1),  i = r/12, n = years·12.
 * At r = 0 it degrades to straight-line p/n.
 */
export function monthlyRepaymentPI(p: number, r: number, years: number): number {
  if (p <= 0) return 0;
  const n = years * 12;
  const i = r / 12;
  if (i === 0) return p / n;
  const factor = Math.pow(1 + i, n);
  return (p * i * factor) / (factor - 1);
}

/** Interest-only monthly repayment — interest on the base, principal untouched. */
export function monthlyRepaymentIO(p: number, r: number): number {
  if (p <= 0) return 0;
  return (p * r) / 12;
}

/** Monthly repayment for either type. */
function monthlyRepayment(
  p: number,
  r: number,
  years: number,
  type: "principal-and-interest" | "interest-only",
): number {
  return type === "interest-only"
    ? monthlyRepaymentIO(p, r)
    : monthlyRepaymentPI(p, r, years);
}

/**
 * Invert the amortisation formula: the maximum principal whose monthly P&I
 * repayment equals `monthlyBudget` at annual rate `r` over `years`. For IO,
 * principal = monthlyBudget · 12 / r.
 */
function maxPrincipalForBudget(
  monthlyBudget: number,
  r: number,
  years: number,
  type: "principal-and-interest" | "interest-only",
): number {
  if (monthlyBudget <= 0) return 0;
  if (type === "interest-only") {
    return r === 0 ? 0 : (monthlyBudget * 12) / r;
  }
  const n = years * 12;
  const i = r / 12;
  if (i === 0) return monthlyBudget * n;
  const factor = Math.pow(1 + i, n);
  // Invert M = p · i·factor / (factor − 1)  ⇒  p = M · (factor − 1) / (i·factor).
  return (monthlyBudget * (factor - 1)) / (i * factor);
}

/** The HEM approximation row matching household + dependants (clamped to table). */
function hemRowFor(
  rows: readonly HemApproximationRow[],
  couple: boolean,
  dependants: number,
): HemApproximationRow {
  const household = couple ? "couple" : "single";
  const candidates = rows.filter((row) => row.household === household);
  if (candidates.length === 0) {
    throw new Error(`no HEM approximation rows for household "${household}"`);
  }
  // Highest row at-or-below the requested dependant count (clamp to the top row).
  let best = candidates[0] as HemApproximationRow;
  for (const row of candidates) {
    if (row.dependants <= dependants && row.dependants >= best.dependants) {
      best = row;
    }
  }
  return best;
}

/**
 * Indicative max borrowable. The serviceability surplus is net income minus the
 * HIGHER of declared expenses and the HEM floor, minus existing debt
 * commitments. The buffer is applied: the surplus is solved against the
 * ASSESSMENT rate (product + APRA 3.0pp buffer), so the indicative loan is what
 * a buffered notional repayment consumes — exactly why the bank's number is
 * lower than the "income × 5" heuristic. An ESTIMATE lenders will assess
 * differently, NOT a pre-approval; HEM ships stamped-indicative.
 */
export function borrowingPower(
  rawInput: BorrowingPowerInput,
  rules: BorrowingPowerRuleSet,
): BorrowingPowerOutput {
  const input: BorrowingPowerParsed = BorrowingPowerInputSchema.parse(rawInput);

  // HEM floor: supplied (user-adjustable) or the documented approximation.
  const hemRow = hemRowFor(rules.hemApproximation, input.couple, input.dependants);
  const hemFloorAnnual = input.hemFloorAnnual ?? hemRow.indicativeAnnualFloor;

  // Lenders FLOOR living expenses at HEM — declaring less than HEM never lifts
  // capacity. Applied expenses = max(declared, HEM floor).
  const appliedAnnualExpenses = Math.max(input.declaredAnnualExpenses, hemFloorAnnual);

  const annualServiceableSurplus = Math.max(
    0,
    input.netAnnualIncome - appliedAnnualExpenses - input.existingAnnualDebtCommitments,
  );

  // The buffer reframed as the assessment rate: capacity is solved at product +
  // 3.0pp, NOT at the product rate.
  const bufferApplied = rules.serviceabilityBuffer;
  const assessmentRate = input.productRate + bufferApplied;

  const monthlyBudget = annualServiceableSurplus / 12;
  const indicativeMaxLoan = round(
    maxPrincipalForBudget(
      monthlyBudget,
      assessmentRate,
      input.loanTermYears,
      input.repaymentType,
    ),
  );

  // DTI framing — APRA ≥6 portfolio rationing, surfaced NOT enforced. We do NOT
  // cap the indicative loan at 6× income (that would be a wrong hard
  // per-borrower cap). We compute the DTI the indicative result implies and flag
  // whether it sits in the band lenders ration at a portfolio level.
  const totalDebt = indicativeMaxLoan + input.existingAnnualDebtCommitments;
  const dtiRatio =
    input.netAnnualIncome > 0 ? totalDebt / input.netAnnualIncome : 0;
  const dti: DtiFraming = {
    ratio: dtiRatio,
    rationingThreshold: rules.dtiRationingThreshold,
    portfolioCapShareOfNewLending: rules.dtiPortfolioCapShareOfNewLending,
    inRationingBand: dtiRatio >= rules.dtiRationingThreshold,
  };

  const out: BorrowingPowerOutput = {
    netAnnualIncome: input.netAnnualIncome,
    appliedAnnualExpenses,
    hemFloorAnnual,
    hemIsEstimate: true,
    annualServiceableSurplus,
    bufferApplied,
    assessmentRate,
    indicativeMaxLoan,
    dti,
  };
  assertNoCreditHandoff(out);
  return out;
}

/**
 * Reframe the 3pp buffer as personal headroom: repayments at the current rate
 * vs at current + the APRA buffer (and a sensitivity strip at +0.25 / +0.5 /
 * +1.0pp). The buffer is the rate the borrower was ASSESSED AGAINST — so the
 * +3.0pp repayment is a real "you were stress-tested to here" figure, not a
 * prediction. Offset reduces the interest-bearing base. Information only.
 */
export function repaymentStress(
  rawInput: RepaymentStressInput,
  rules: BorrowingPowerRuleSet,
): RepaymentStressOutput {
  const input: RepaymentStressParsed = RepaymentStressInputSchema.parse(rawInput);

  const interestBearingBase = Math.max(0, input.loanAmount - input.offsetBalance);
  const bufferApplied = rules.serviceabilityBuffer;

  const baseAt = (rate: number) =>
    monthlyRepayment(
      interestBearingBase,
      rate,
      input.loanTermYears,
      input.repaymentType,
    );

  const currentMonthlyRepayment = round(baseAt(input.currentRate));

  // Sensitivity strip + the APRA buffer scenario.
  const deltas: { delta: number; isBuffer?: boolean }[] = [
    { delta: 0 },
    { delta: 0.0025 },
    { delta: 0.005 },
    { delta: 0.01 },
    { delta: bufferApplied, isBuffer: true },
  ];

  const scenarios: StressScenario[] = deltas.map(({ delta, isBuffer }) => {
    const rate = input.currentRate + delta;
    const monthlyRepaymentVal = round(baseAt(rate));
    return {
      rateDelta: delta,
      rate,
      monthlyRepayment: monthlyRepaymentVal,
      extraVsCurrentMonthly: monthlyRepaymentVal - currentMonthlyRepayment,
      ...(isBuffer ? { isApraBuffer: true } : {}),
    };
  });

  const buffered = scenarios.find((s) => s.isApraBuffer) as StressScenario;

  const out: RepaymentStressOutput = {
    loanAmount: input.loanAmount,
    offsetBalance: input.offsetBalance,
    interestBearingBase,
    currentMonthlyRepayment,
    bufferApplied,
    bufferedMonthlyRepayment: buffered.monthlyRepayment,
    bufferedExtraMonthly: buffered.extraVsCurrentMonthly,
    scenarios,
  };
  assertNoCreditHandoff(out);
  return out;
}
